import React, { Fragment, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { FoodBank } from '@mui/icons-material';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';

const defaultValues = {
  display_name: '',
  is_active: true,
};

const validationSchema = Yup.object().shape({
  display_name: Yup.string().required('Required'),

  is_active: Yup.boolean().required(),
});

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | SKU Types';
  }, []);
  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [appliedFilters, setAppliedFilters] = React.useState([]);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    uoms: [],
  });
  const [displayName, setDisplayName] = React.useState('');
  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    setValue,
    // clearErrors,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    setDataIsFetching(true);

    const fetchData = async () => {
      try {
        let requestUrl = `/sku-types?per_page=${perPage}&page=${page}`;

        if (statusFilter === 'ACTIVE') {
          requestUrl += '&is_active=true';
          setAppliedFilters(['is_active']);
        } else if (statusFilter === 'INACTIVE') {
          requestUrl += '&is_active=false';
          setAppliedFilters(['is_active']);
        } else {
          setAppliedFilters([]);
        }
        console.log(requestUrl);
        const { data } = await userRequest().get(requestUrl);

        setTableData({
          count: data.total,
          uoms: data.skuTypes,
        });

        setDataIsFetching(false);
      } catch (error) {
        console.error(error.response);
        setDataIsFetching(false);
      }
    };

    fetchData();
  }, [statusFilter, perPage, page]);

  function setStatus(id, data) {
    userRequest()
      .post(`/sku-types/update/${id}`, data)
      .then(() => {
        let uoms = [];
        if (meetFilterConditions(data)) {
          uoms = tableData.uoms.map((uom) => {
            if (id === uom.id) uom.is_active = !uom.is_active;
            return uom;
          });
        } else uoms = tableData.uoms.filter((u) => u.id !== id);
        setTableData({
          count: uoms.length,
          uoms,
        });
      })
      .catch(() => {});
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    if (statusFilter === 'ALL') {
      return true;
    }
    if (appliedFilters.length > 0) return statusFilter === data.is_active;

    return true;
  }

  function getFilters() {
    if (statusFilter === 'ACTIVE') {
      return ['Status: Active'];
    } else if (statusFilter === 'INACTIVE') {
      return ['Status: Inactive'];
    } else {
      return [];
    }
  }

  const onSubmit = (data) => {
    console.log('called');
    if (data) {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });
      let api = modalState.update ? `/sku-types/update/${modalState.update}` : '/sku-types';

      const postData = !modalState.update ? { ...data, display_name: displayName } : data;

      userRequest()
        .post(api, postData)
        .then((res) => {
          toast.dismiss('create');
          toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
          let uom = res.data.skuType;
          let uoms = [];
          if (modalState.update) {
            uoms = meetFilterConditions(uom)
              ? tableData.uoms.map((u) => {
                  return u.id === uom.id ? uom : u;
                })
              : tableData.uoms.filter((u) => u.id !== uom.id);
          } else {
            uoms = meetFilterConditions(uom) ? [...tableData.uoms, uom] : tableData.uoms;
          }
          setTableData({
            count: uoms.length,
            uoms,
          });
          setModalState({ update: true, open: false });
          reset(defaultValues);
        })
        .catch(({ response }) => {
          toast.dismiss('create');
          toast.error(response.data.message, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        });
    }
  };

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(
        Object.keys(defaultValues).reduce((reduced, curr) => {
          reduced[curr] = row[curr];
          return reduced;
        }, {})
      );
      // clearErrors();
    },
    toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
  };
  const columns = [
    {
      field: 'display_name',
      headerName: 'SKU Type',
      flex: 0.3,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'left',
      flex: 2,

      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumb icon={<FoodBank />} title="Manage" parent="SKU Types" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <div className="form-group row mx-0">
            <FormControl>
              <InputLabel>Status</InputLabel>
              <Select
                name="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                style={{
                  fontStyle: statusFilter === 'All' ? 'italic' : 'normal',
                }}
              >
                <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                  All
                </MenuItem>
                <MenuItem value={'ACTIVE'}>Active</MenuItem>
                <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          setModalState({ open: true, update: false });
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ open: false, update: false });
          reset(defaultValues);
        }}
        size="lg"
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ open: false, update: false });
            reset(defaultValues);
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} SKU Type
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              {!modalState.update ? (
                <div className="form-group row">
                  <TextField
                    label="Sku"
                    variant="outlined"
                    {...register('display_name', {
                      required: 'Sku is required', // Add required rule
                    })}
                    onChange={(val) => {
                      setDisplayName(val.target.value);
                      setValue('display_name', val.target.value, { shouldValidate: true });
                    }}
                    // error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                    // helperText={errors.display_name?.message && isSubmitted ? errors.display_name.message : ''}
                    inputProps={{
                      style: {
                        color: 'black',
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="form-group row">
                  <TextField
                    label="Name"
                    variant="outlined"
                    {...register('display_name', {
                      required: 'Name is required', // Add required rule
                    })}
                    onChange={(val) => {
                      setDisplayName(val.target.value);
                      setValue('display_name', val.target.value, {
                        shouldValidate: true,
                      });
                    }}
                    // error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                    // helperText={errors.display_name?.message}
                    inputProps={{
                      style: {
                        color: 'black',
                      },
                    }}
                  />
                </div>
              )}
              {modalState.update && (
                <div className="d-flex justify-content-start my-4">
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                </div>
              )}
              <button className="btn btn-primary" type="submit">
                {modalState.update ? 'Save' : 'Create'}
              </button>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.uoms}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'display_name', sort: 'asc' }]}
        />
      </div>
    </Fragment>
  );
};

export default Manage;
